import { useMemo } from 'react';
import { useCourtCoverage, type CourtCoverage } from '@/api/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { GlobeSpinner } from '@/components/ui/globe-spinner';
import { PageBanner } from '@/components/layout/PageBanner';
import { SortableHeader } from '@/components/ui/sortable-header';
import { TablePager } from '@/components/ui/table-pager';
import { Progress } from '@/components/ui/progress';
import { useClientSort } from '@/hooks/use-client-sort';
import { formatNumber } from '@/lib/utils';
import { BarChart3, Scale, Building2, Gavel } from 'lucide-react';

const coverageColor = (pct: number) => {
  if (pct < 20) return 'oklch(60% 0.22 25)';
  if (pct < 50) return 'oklch(70% 0.16 85)';
  return 'oklch(65% 0.18 145)';
};

const jurisdictionVariant = (j: string) => {
  const map: Record<string, string> = { federal: 'sky', state: 'violet' };
  return (map[j] ?? 'secondary') as never;
};

const levelVariant = (l: string) => {
  const map: Record<string, string> = {
    supreme: 'rose', appellate: 'orange', district: 'teal',
    trial: 'indigo', bankruptcy: 'warning', limited: 'secondary',
  };
  return (map[l] ?? 'secondary') as never;
};

const PAGE_SIZE = 20;

const SORT_FIELDS: Record<string, (c: CourtCoverage) => string | number | null | undefined> = {
  name: c => c.name || '',
  jurisdiction: c => c.jurisdiction || '',
  level: c => c.level || '',
  our_case_count: c => c.our_case_count,
  est_annual_cases: c => c.est_annual_cases,
  coverage_pct: c => c.coverage_pct,
  has_bulk_source: c => c.has_bulk_source ? 1 : 0,
  has_api_source: c => c.has_api_source ? 1 : 0,
};

export default function CourtCoveragePage() {
  const { data, isLoading } = useCourtCoverage();

  const { paged, page, setPage, total, sortField, sortDir, toggleSort } = useClientSort<CourtCoverage>({
    data: data?.courts,
    defaultField: 'our_case_count',
    defaultDir: 'desc',
    fields: SORT_FIELDS,
    pageSize: PAGE_SIZE,
  });

  // Stats for banner
  const stats = useMemo(() => {
    if (!data?.courts) return { totalCourts: 0, totalCases: 0, withBulk: 0, withApi: 0 };
    return {
      totalCourts: data.courts.length,
      totalCases: data.courts.reduce((s, c) => s + (c.our_case_count ?? 0), 0),
      withBulk: data.courts.filter(c => c.has_bulk_source).length,
      withApi: data.courts.filter(c => c.has_api_source).length,
    };
  }, [data?.courts]);

  return (
    <div className="space-y-6 animate-fade-up p-1">
      <PageBanner>
        <div className="banner-chip">
          <h1 className="text-2xl font-bold banner-title">Court Coverage</h1>
        </div>
        <div className="flex gap-4 flex-wrap">
          <div className="banner-chip">
            <Building2 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm">{stats.totalCourts} courts</span>
          </div>
          <div className="banner-chip">
            <Scale className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm">{formatNumber(stats.totalCases)} cases</span>
          </div>
          <div className="banner-chip">
            <Gavel className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm">{stats.withApi} with API source</span>
          </div>
        </div>
      </PageBanner>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <span className="text-amber-700 dark:text-amber-400">Coverage by Court</span>
            <Badge variant="info" className="ml-1">{total}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <GlobeSpinner label="Loading court coverage..." />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortableHeader label="Court" field="name" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Jurisdiction" field="jurisdiction" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Level" field="level" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Our Cases" field="our_case_count" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Est. Annual" field="est_annual_cases" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="w-[180px]">
                      <SortableHeader label="Coverage" field="coverage_pct" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Bulk" field="has_bulk_source" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="API" field="has_api_source" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.map((c, idx) => {
                    const covPct = (c.coverage_pct ?? 0) * 100;
                    return (
                      <TableRow key={c.court_code} className={idx % 2 === 0 ? '' : 'bg-table-row-even'}>
                        <TableCell className="font-medium max-w-[280px] truncate" title={c.name}>
                          {c.short_name || c.name}
                        </TableCell>
                        <TableCell>
                          <Badge variant={jurisdictionVariant(c.jurisdiction)} className="text-xs capitalize">
                            {c.jurisdiction}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={levelVariant(c.level)} className="text-xs capitalize">
                            {c.level}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">{formatNumber(c.our_case_count)}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">
                          {c.est_annual_cases ? formatNumber(c.est_annual_cases) : '-'}
                        </TableCell>
                        <TableCell>
                          {c.coverage_pct != null ? (
                            <div className="flex items-center gap-3">
                              <Progress
                                value={covPct}
                                max={100}
                                color={coverageColor(covPct)}
                                className="h-2 flex-1"
                              />
                              <span className="text-xs font-mono w-12 text-right">{covPct.toFixed(1)}%</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {c.has_bulk_source
                            ? <Badge variant="success" className="text-xs">Yes</Badge>
                            : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          {c.has_api_source
                            ? <Badge variant="success" className="text-xs">Yes</Badge>
                            : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {paged.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3">
                          <Scale className="h-10 w-10 text-muted-foreground/40" />
                          <p className="text-muted-foreground">No courts registered yet</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <TablePager page={page} onPageChange={setPage} total={total} pageSize={PAGE_SIZE} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
